/* storybook-check-ignore */
import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { useAuth } from '@opendoor/auth-fe';

import { TourRequestFragment } from '__generated__/athena';

import { getAthenaClient } from 'components/api';

import { CustomerTours, CustomerToursContext } from './customerToursContext';

interface CustomerToursProviderProps {
  addressToken: string;
  children: ReactNode;
}

const CustomerToursProvider: FC<CustomerToursProviderProps> = ({ addressToken, children }) => {
  const { authentication, user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [tours, setTours] = useState<TourRequestFragment[] | null>(null);

  const fetchCustomerTours = useCallback(async () => {
    setLoading(true);

    if (!user?.customerUuid) {
      setTours([]);
      // If authenticating, we'll get a state update (probably with a customer ID) soon so don't
      // toggle out of loading.
      if (authentication.state !== 'authenticating') {
        setLoading(false);
      }
      return;
    }

    const result = await getAthenaClient().GetMarketplacePropertyTours({
      customerId: user.customerUuid,
    });

    const tours = result?.touring.listTourRequestsByCustomer?.tourRequests.filter(
      (request) =>
        request.addressId === addressToken &&
        request.state !== 'TERMINATED' &&
        request.state !== 'UNKNOWN_STATE',
    );

    if (tours) {
      setTours(tours);
    }

    setLoading(false);
  }, [authentication, user]);

  useEffect(() => {
    fetchCustomerTours();
  }, [fetchCustomerTours]);

  const cancelTour = useCallback(
    async (tourId: string) => {
      setCancelling(true);

      await getAthenaClient().CancelTour({ id: tourId, reason: 'TOURING_CUSTOMER_CANCELED' });

      await fetchCustomerTours();

      setCancelling(false);
    },
    [fetchCustomerTours],
  );

  const context: CustomerTours = useMemo(
    () => ({
      loading,
      cancelling,
      tours: tours ?? [],
      refetch: fetchCustomerTours,
      cancelTour,
    }),
    [loading, cancelling, tours, fetchCustomerTours, cancelTour],
  );

  return <CustomerToursContext.Provider value={context}>{children}</CustomerToursContext.Provider>;
};

export default CustomerToursProvider;
